/* eslint-disable no-param-reassign */
import axios from "axios";
import { toast } from "react-toastify";
import i18next from "i18next";
import { API_URL } from "../constant/url";
import { store } from "./index";
import { clearAuth, logout } from "./slices/authSlice";

const request = axios.create({ withCredentials: true });

let REQUEST_URLS = [];
let globalLoaderCount = 0;
// One-shot guard: concurrent 401s share a single logout + toast until the user logs in again.
let sessionExpiredHandled = false;

export const resetSessionExpiredState = () => {
	sessionExpiredHandled = false;
};

// Function to translate error messages
const translateErrorMessage = (messageKey) => {
	if (!messageKey) return "An unknown error occurred";

	// Try to translate using i18next
	const translated = i18next.t(messageKey);

	// If the translation key returns the same text, it might not be a valid key
	// In that case, use the original message
	return translated === messageKey && messageKey.indexOf(".") > -1
		? messageKey
		: translated;
};

request.interceptors.request.use(
	(config) => {
		if (!config.skipGlobalLoader) {
			document.body.classList.add("loading-indicator");
			globalLoaderCount++;
		}
		REQUEST_URLS.push(config.url);
		config.baseURL = `${API_URL}`;
		// Auth is handled via httpOnly cookie - no Authorization header needed.
		// The cookie is sent automatically because the instance was created with
		// withCredentials: true.
		return config;
	},
	(error) => Promise.reject(error),
);

request.interceptors.response.use(
	(response) => {
		const index = REQUEST_URLS.indexOf(response.config.url);
		if (index > -1) {
			REQUEST_URLS.splice(index, 1);
		}
		if (!response.config.skipGlobalLoader) {
			globalLoaderCount = Math.max(0, globalLoaderCount - 1);
		}
		if (!globalLoaderCount) {
			document.body.classList.remove("loading-indicator");
		}
		const { data, headers } = response;

		// Successful login - allow future session-expiry handling
		if (response.config?.url === "login") {
			resetSessionExpiredState();
		}

		if (data instanceof ArrayBuffer) {
			if (
				headers["content-type"] ===
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
			) {
				const url = window.URL.createObjectURL(
					new Blob([data], {
						type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
					}),
				);
				const fileName = headers["content-disposition"]
					.split("filename=")[1]
					.split(";")[0];
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", fileName.replaceAll('"', ""));
				document.body.appendChild(link);
				link.click();
				return "";
			}
			const parsedResponse = JSON.parse(new TextDecoder().decode(data));
			return parsedResponse?.data || "";
		}

		// Return the data property for regular API responses
		return data.data !== undefined ? data.data : data;
	},
	async (error) => {
		const index = REQUEST_URLS.indexOf(error.config?.url);
		if (index > -1) {
			REQUEST_URLS.splice(index, 1);
		}
		if (!error.config?.skipGlobalLoader) {
			globalLoaderCount = Math.max(0, globalLoaderCount - 1);
		}
		if (!globalLoaderCount) {
			document.body.classList.remove("loading-indicator");
		}

		// Handle error responses
		if (error.response) {
			// Handle blob error responses (when responseType is blob)
			if (error.response.data instanceof Blob) {
				try {
					const errorText = await error.response.data.text();
					const errorData = JSON.parse(errorText);

					// Replace the blob with the parsed JSON data
					error.response.data = errorData;
				} catch (parseError) {
					// If parsing fails, create a generic error response
					error.response.data = {
						message: "INTERNAL_SERVER_ERROR",
						statusCode: error.response.status,
					};
				}
			}

			// Handle 401 Unauthorized errors by logging out the user.
			// Skip this for the login endpoint - a 401 there just means wrong credentials,
			// not an expired session, so the authSlice rejected handler shows the error.
			const isLoginRequest = error.config?.url === "login";
			if (
				error.response.status === 401 &&
				!isLoginRequest &&
				!sessionExpiredHandled
			) {
				sessionExpiredHandled = true;

				// Clear Redux immediately so ProtectedLayout navigates to /login via SPA.
				store.dispatch(clearAuth());
				// Clear the httpOnly cookie on the server (ignore failures - session is already invalid).
				store.dispatch(logout()).catch(() => {});

				toast.error(translateErrorMessage("errors.UNAUTHORIZED"), {
					position: "bottom-center",
					autoClose: 3000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					toastId: "session-expired",
				});

				return Promise.reject(error.response.data);
			}

			// Preserve full response (message key + data for interpolation) so
			// callers can use t(message, data). Only translate when there is no
			// interpolation data (so UI can show a plain translated message).
			const responseData = error.response?.data;
			const dataObj =
				responseData &&
				typeof responseData === "object" &&
				!Array.isArray(responseData) &&
				responseData.data;
			const hasInterpolation =
				dataObj &&
				typeof dataObj === "object" &&
				!Array.isArray(dataObj) &&
				Object.keys(dataObj).length > 0;

			const messageKey = responseData?.message;

			let rejectPayload;
			if (typeof responseData === "string") {
				rejectPayload = { message: responseData };
			} else if (responseData && hasInterpolation) {
				// Keep message as key and data for interpolation (no translation here)
				rejectPayload = {
					...responseData,
					originalMessage: messageKey,
				};
			} else {
				// No interpolation: translate message for backward compatibility
				let errorMessage;
				if (messageKey) {
					errorMessage = translateErrorMessage(messageKey);
				} else if (error.response?.status) {
					errorMessage = translateErrorMessage(
						`errors.${error.response.status}`,
					);
				} else {
					errorMessage = translateErrorMessage(
						"errors.INTERNAL_SERVER_ERROR",
					);
				}
				rejectPayload = {
					...(responseData && typeof responseData === "object"
						? responseData
						: {}),
					message: errorMessage,
					originalMessage: messageKey,
				};
			}

			return Promise.reject(rejectPayload);
		}

		// Handle network errors
		if (error.request) {
			const networkErrorMessage = translateErrorMessage(
				"errors.SERVICE_UNAVAILABLE",
			);
			return Promise.reject({
				...error.request,
				message: networkErrorMessage,
			});
		}

		// Handle other errors
		const genericErrorMessage = translateErrorMessage(
			"errors.INTERNAL_SERVER_ERROR",
		);
		return Promise.reject({
			...error,
			message: genericErrorMessage,
		});
	},
);

export default request;
